import { ref, computed, watch } from "vue"
import { api, supabase } from "@/lib/api"
import { useAuth } from "@/composables/auth/useAuth"
import { debug, warn, error as logError } from "@/utils/debug"
import type { RealtimePostgresChangesPayload } from "@supabase/supabase-js"

export interface Notification {
  id: string
  user_id: string
  type: string
  message: string
  read: boolean
  created_at: string
  actor_id: string | null
  actor_username: string | null
  actor_profile_pic_url: string | null
  reference_id: string | null
  metadata: Record<string, any>
}

// Singleton state for notifications across the app
const notifications = ref<Notification[]>([])
const isLoading = ref(false)
let notificationsChannel: ReturnType<typeof supabase.channel> | null = null
let isSubscribed = false
let isInitialized = false
let isFetching = false

// Get user from auth composable at module level
const { user } = useAuth()

export function useNotifications() {
  const unreadCount = computed(() => notifications.value.filter((n) => !n.read).length)

  /**
   * Fetch notifications for the current user
   */
  async function fetchNotifications(unreadOnly = false, limit = 50, offset = 0): Promise<void> {
    // Prevent concurrent fetches
    if (isFetching) {
      debug("Fetch already in progress, skipping")
      return
    }

    isFetching = true
    isLoading.value = true
    try {
      const { data, error } = await api<Notification[]>("fetchNotificationsNotification", {
        limit,
        offset,
        unread_only: unreadOnly,
      })

      if (error) {
        logError("Failed to fetch notifications:", error)
        return
      }

      notifications.value = data || []
    } finally {
      isLoading.value = false
      isFetching = false
    }
  }

  /**
   * Mark a specific notification as read
   */
  async function markAsRead(notificationId: string): Promise<boolean> {
    try {
      const { error } = await api("markNotificationReadNotification", {
        notification_id: notificationId,
      })

      if (error) {
        logError("Failed to mark notification as read:", error)
        return false
      }

      // Update local state
      const notification = notifications.value.find((n) => n.id === notificationId)
      if (notification) {
        notification.read = true
      }

      return true
    } catch (err) {
      logError("Error marking notification as read:", err)
      return false
    }
  }

  /**
   * Mark all notifications as read
   */
  async function markAllAsRead(): Promise<boolean> {
    try {
      const { error } = await api("markAllNotificationsReadNotification", {})

      if (error) {
        logError("Failed to mark all notifications as read:", error)
        return false
      }

      // Update local state
      notifications.value.forEach((n) => {
        n.read = true
      })

      return true
    } catch (err) {
      logError("Error marking all notifications as read:", err)
      return false
    }
  }

  /**
   * Delete a notification
   */
  async function deleteNotification(notificationId: string): Promise<boolean> {
    try {
      const { error } = await api("deleteNotification", {
        notification_id: notificationId,
      })

      if (error) {
        logError("Failed to delete notification:", error)
        return false
      }

      // Remove from local state
      notifications.value = notifications.value.filter((n) => n.id !== notificationId)

      return true
    } catch (err) {
      logError("Error deleting notification:", err)
      return false
    }
  }

  /**
   * Get notification icon based on type
   */
  function getNotificationIcon(type: string): string {
    switch (type) {
      case "follow_request_received":
        return "person-add-outline"
      case "follow_request_accepted":
        return "checkmark-circle-outline"
      case "new_follower":
        return "person-outline"
      case "follow":
        return "people-outline"
      case "club_event":
        return "calendar-outline"
      case "review_reply":
        return "chatbox-outline"
      default:
        return "notifications-outline"
    }
  }

  /**
   * Setup realtime subscription for new notifications
   */
  function setupRealtimeSubscription() {
    if (isSubscribed || !user.value) {
      return
    }

    debug("Setting up realtime subscription for notifications")

    notificationsChannel = supabase
      .channel("notifications-changes")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "books",
          table: "notifications",
          filter: `user_id=eq.${user.value.id}`,
        },
        (payload: RealtimePostgresChangesPayload<Notification>) => {
          debug("New notification received:", payload)
          // Add new notification to the beginning of the list
          const newNotification = payload.new as Notification
          notifications.value = [newNotification, ...notifications.value]
        },
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "books",
          table: "notifications",
          filter: `user_id=eq.${user.value.id}`,
        },
        (payload: RealtimePostgresChangesPayload<Notification>) => {
          debug("Notification updated:", payload)
          // Update existing notification
          const updatedNotification = payload.new as Notification
          const index = notifications.value.findIndex((n) => n.id === updatedNotification.id)
          if (index !== -1) {
            notifications.value[index] = updatedNotification
          }
        },
      )
      .on(
        "postgres_changes",
        {
          event: "DELETE",
          schema: "books",
          table: "notifications",
          filter: `user_id=eq.${user.value.id}`,
        },
        (payload: RealtimePostgresChangesPayload<Notification>) => {
          debug("Notification deleted:", payload)
          // Remove deleted notification
          const deletedId = (payload.old as Notification).id
          notifications.value = notifications.value.filter((n) => n.id !== deletedId)
        },
      )
      .subscribe((status: string) => {
        if (status === "SUBSCRIBED") {
          debug("✅ Subscribed to notifications changes")
          isSubscribed = true
        } else if (status === "CHANNEL_ERROR") {
          warn("⚠️ Notifications channel error")
          isSubscribed = false
        } else if (status === "TIMED_OUT") {
          warn("⚠️ Notifications channel timed out")
          isSubscribed = false
        } else if (status === "CLOSED") {
          debug("Notifications channel closed")
          isSubscribed = false
        }
      })
  }

  /**
   * Cleanup realtime subscription
   */
  function cleanupRealtimeSubscription() {
    if (notificationsChannel) {
      debug("Cleaning up notifications realtime subscription")
      supabase.removeChannel(notificationsChannel)
      notificationsChannel = null
      isSubscribed = false
    }
  }

  // Initialize the watcher only once (singleton pattern)
  if (!isInitialized) {
    isInitialized = true

    // Watch for user login/logout and setup subscription
    watch(
      user,
      (newUser, oldUser) => {
        if (newUser) {
          // User is logged in (either on mount or after login)
          debug("User available, fetching notifications")
          setupRealtimeSubscription()
          fetchNotifications()
        } else if (!newUser && oldUser) {
          // User logged out
          debug("User logged out, clearing notifications")
          cleanupRealtimeSubscription()
          notifications.value = []
        }
      },
      { immediate: true },
    )
  }

  return {
    notifications,
    isLoading,
    unreadCount,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    getNotificationIcon,
  }
}
